// stores/companyStore.js
'use client';

import { useState, useEffect, useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payloadStr, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateCompanyDataList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/company/search');
};

// ==========================================
// 1. ORIGINAL HOOK (DIPERTAHANKAN)
// ==========================================
export const useCompanyList = () => {
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanies = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        let allCompanies = [];
        let start = 0;
        let length = 100;
        let hasMore = true;

        while (hasMore && isMounted) {
          const fb = new FilterBuilder().setPaging(start, length).setOrder(['name'], true).addJoin('business_type', ['name']);
          const payload = fb.build();

          const res = await fleetApi.post(`/company/search${getTzParam()}`, payload);
          const raw = res?.data?.data ?? res?.data ?? [];

          allCompanies = allCompanies.concat(raw);

          if (raw.length < length) {
            hasMore = false;
          } else {
            start += length;
          }
        }

        const options = allCompanies.map((company) => ({
          value: company.id,
          label: company.name,
          _raw: company
        }));

        if (isMounted) {
          setCompanyOptions(options);
        }
      } catch (error) {
        if (isMounted) {
          setIsError(true);
        }
        console.log(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCompanies();

    return () => {
      isMounted = false;
    };
  }, []);

  return { companyOptions, isLoading, isError };
};

// ==========================================
// 2. NEW HOOK: DATATABLE LIST (SWR)
// ==========================================
export const useCompanyDataList = (page = 0, rowsPerPage = 1000, searchKey = 'name', searchValue = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['name'], true).addJoin('business_type', ['name']);
    const tableName = searchKey === 'business_type' ? 'm_business_type' : null;
    const filterLike = searchKey === 'business_type' ? 'name' : searchKey;

    const sv = String(searchValue ?? '').trim();
    if (sv !== '') {
      if (searchKey === 'all' || !searchKey) {
        fb.like('name', '', 'AND');
        fb.addFilter('name', sv);
        fb.addFilter('code', sv);
        fb.addFilter('city', sv);
        fb.addFilter('country', sv);
        fb.addFilter('name', sv, 'm_business_type');
      } else {
        fb.like(filterLike || 'name', sv, 'AND', tableName);
      }
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue]);

  const key = useMemo(() => ['/company/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const rawList = Array.isArray(data?.data?.data)
    ? data.data.data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
        ? data
        : [];

  const total =
    data?.data?.totalRecord ??
    data?.data?.totalRecords ??
    data?.data?.dataSize ??
    data?.totalRecord ??
    data?.totalRecords ??
    rawList.length;

  return {
    data: rawList,
    totalRecord: total,
    isLoading,
    isError: error
  };
};

// ==========================================
// 3. CRUD ENDPOINTS
// ==========================================

export const getCompanyById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/company/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    // Fallback jika response /id tidak sesuai format, hit search endpoint
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/company/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch company by id:', err);
    throw err;
  }
};

export const createCompany = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/company/new${getTzParam()}`, body);

    if (response?.code >= 400 || response?.status === false) {
      throw { response: { data: response } };
    }
    revalidateCompanyDataList();
    return response.data;
  } catch (err) {
    console.error('Error creating company:', err);
    throw err;
  }
};

export const updateCompany = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/company/edit/${id}${getTzParam()}`, body);

    if (response?.code >= 400 || response?.status === false) {
      throw { response: { data: response } };
    }
    revalidateCompanyDataList();
    mutate(`/company/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating company:', err);
    throw err;
  }
};

export const deleteCompany = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/company/delete/${id}${getTzParam()}`);

    if (response?.code >= 400 || response?.status === false) {
      throw { response: { data: response } };
    }
    revalidateCompanyDataList();
    return response.data;
  } catch (err) {
    console.error('Error deleting company:', err);
    throw err;
  }
};
